import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BulkSelectEmployeeDialogComponent, ConfirmationDialogComponent } from '@app/components';
import { AnalyticEvents, FeatureFlag, SocketEventTypes } from '@app/enums';
import { CompanyDocument } from '@app/models/documents/company-document.model';
import { DocumentAssignment } from '@app/models/documents/document-assignment.model';
import { DocumentTag } from '@app/models/documents/document-tag.model';
import { SigningTemplateSlot } from '@app/models/documents/signing-template-slot.model';
import { SigningTemplate } from '@app/models/documents/signing-template.model';
import { Employee } from '@app/models/employee/employee.model';
import { SlotAssignment } from '@app/modules/documents-v2/classes';
import { DocumentAssignmentService } from '@app/modules/documents-v2/services/document-assignment.service';
import { CompanyDocumentForm, MultiSignerAssignmentForm } from '@app/modules/forms/documents/components';
import { DocumentsFormsModule } from '@app/modules/forms/documents/documents-forms.module';
import {
    OfferLetterCreationReferer,
    TEMPLATE_SHOW,
} from '@app/modules/offer-letter/types/offer-letter-referer-view.type';
import { HireEmployee } from '@app/modules/settings/meta/module-feature-permissions.meta';
import { PlatformModule } from '@app/platform.module';
import { AnalyticService, AuthService, FileHelperService, NotifyService, SocketService } from '@app/services';
import { FeatureService } from '@app/services/feature.service';
import { MenuService } from '@app/services/menu/menu.service';
import { COMPANY_DOCUMENTS_TAB_MENU } from '@documents-v2/menus/company-documents-show-menu.service';
import { DocumentDetailsComponent } from '@documents-v2/standalone-components/document-details/document-details.component';
import { CompanyDocumentDeletionModal } from '@documents-v2/views';
import { TranslateModule } from '@ngx-translate/core';
import { cloneDeep } from 'lodash-es';
import { Subscription } from 'rxjs';

type CompanyDocumentsHeaderDialogType = 'MULTI_SIGNER' | 'RENAME_EDIT_TAGS' | 'DETAILS' | 'DEFAULT_COSIGNERS';

@Component({
    standalone: true,
    imports: [
        CommonModule,
        PlatformModule,
        TranslateModule,
        FormsModule,
        DocumentsFormsModule,
        DocumentDetailsComponent,
        CompanyDocumentDeletionModal,
    ],
    selector: 'app-documents-company-document-show-header',
    templateUrl: './company-document-show-header.template.html',
    styleUrls: ['./company-document-show-header.style.scss'],
})
export class CompanyDocumentShowHeaderComponent implements OnDestroy, OnInit {
    @ViewChild('appEmployeeSelection', { static: true }) appEmployeeSelection: BulkSelectEmployeeDialogComponent;
    @ViewChild('companyDocumentDeletionModal') companyDocumentDeletionModal: CompanyDocumentDeletionModal;
    @ViewChild('lackingSigningTemplateModal', { static: true })
    lackingSigningTemplateModal: ConfirmationDialogComponent;
    @ViewChild('multiSignerAssignmentForm', { static: true })
    multiSignerAssignmentForm: MultiSignerAssignmentForm;
    @ViewChild('defaultCosignersForm', { static: true }) defaultCosignersForm!: MultiSignerAssignmentForm;
    @ViewChild('bulkRemindConfirm', { static: true }) bulkRemindConfirm: ConfirmationDialogComponent;

    // Models
    @Input() allTags: DocumentTag[] = [];
    @Input() set companyDocument(document: CompanyDocument) {
        this.hasCosigners = document?.signingTemplate?.slots ? document.signingTemplate.slots.length > 1 : false;
        this.assigningOrder = document?.signingTemplate?.slots.some((s: SigningTemplateSlot) => s.order);
        this.assigningOrderDefaultSigner = document?.signingTemplate?.slots.some((s: SigningTemplateSlot) => s.order);
        this._companyDocument = document;

        // Adding this to decouple it from the other multi signer dialog as they were using the same signing template variable
        this.defaultSigningTemplate = cloneDeep(document?.signingTemplate);
    }
    get companyDocument(): CompanyDocument | undefined {
        return this._companyDocument;
    }

    @Input() initialDocumentTags: DocumentTag[] = [];

    // Presentation stuff
    @Input() backRoute = '..';
    @Input() showTemplateEditButton = false;
    @Input() showRemindButton = false;
    @Input() showMeatballMenu = false;

    @Output() isLoading: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() reload: EventEmitter<null> = new EventEmitter<null>();

    editDocumentPermission = CompanyDocument.permission.edit;
    createAssignments = CompanyDocument.permission.edit;
    hirePermissions = HireEmployee.permissions;

    assigningOrder = false;
    assigningOrderDefaultSigner = false;
    _companyDocument?: CompanyDocument;

    offerLetterReferer: OfferLetterCreationReferer = TEMPLATE_SHOW;
    queryParamSubscription: Subscription;
    folderId: number;
    slotAssignments: SlotAssignment[] = [];
    defaultSigningTemplate: SigningTemplate;

    hasCustomFieldAssignments = false;
    companyDocumentBackRoute = '/documents/center';

    currentlyOpenDialog: CompanyDocumentsHeaderDialogType | undefined;

    hasCosigners = false;
    multiSignerAssignmentFormVisible = true;

    protected becameEditable = false;
    protected subscription: Subscription = null;

    constructor(
        private fileHelper: FileHelperService,
        private notify: NotifyService,
        private router: Router,
        private route: ActivatedRoute,
        private socket: SocketService,
        private documentAssignmentService: DocumentAssignmentService,
        private auth: AuthService,
        protected analyticService: AnalyticService,
        private features: FeatureService,
        @Inject(COMPANY_DOCUMENTS_TAB_MENU) public menuService: MenuService
    ) {
        this.subscription = this.socket.getCompanyChannel(SocketEventTypes.signingTemplateReady).subscribe((event) => {
            if (event.templateId && event.templateId === this.companyDocument?.signingTemplate?.id) {
                this.becameEditable = true;
            }
        });
    }

    ngOnDestroy(): void {
        this.queryParamSubscription.unsubscribe();
        this.subscription.unsubscribe();
    }

    bulkRemind(): void {
        this.bulkRemindConfirm.open().then((yes: boolean) => {
            if (yes) {
                this.companyDocument
                    .notify()
                    .then(() => this.notify.success('document.remindersSent'))
                    .catch((err) => this.notify.error(err));
            }
        });
    }

    async ngOnInit(): Promise<void> {
        if (!this.auth.isAdmin() || !this.auth.can(CompanyDocument.permission.edit)) {
            // Employee route
            const isDocumentsV2 = await this.features.has(FeatureFlag.documentsV2);
            this.companyDocumentBackRoute = isDocumentsV2 ? '/profile/documents-v2/' : '/profile/documents/';
        } else {
            // Admin route
            this.companyDocumentBackRoute = this.backRoute;
        }

        this.queryParamSubscription = this.route.queryParams.subscribe(async (params) => {
            this.folderId = params?.folderId;
            if (this.folderId) {
                this.companyDocumentBackRoute = this.companyDocumentBackRoute + '/folders/' + this.folderId.toString();
            }
        });
    }

    async onSave(form: CompanyDocumentForm): Promise<void> {
        if (!form.valid) {
            return;
        }

        this.isLoading.emit(true);

        try {
            const selectedTags = [
                ...(await DocumentTag.saveNewTags(form.getNewTags())),
                ...form.getExistingSelectedTags(),
            ];
            const toDetach = this.initialDocumentTags
                .filter((tag: DocumentTag) => !selectedTags.includes(tag))
                .map((tag: DocumentTag) => tag.id);

            await this.companyDocument.detach('documentTags', toDetach).attach('documentTags', selectedTags).save();

            this.notify.success('document.documentUpdated');
            this.closeDialog();
        } catch (e) {
            this.notify.error(e);
        }

        this.reload.emit();
        this.isLoading.emit(false);
    }

    async onSaveDefaultCosigners(form: NgForm): Promise<void> {
        form.onSubmit(null);

        if (!form.valid) {
            return;
        }

        const documentAssignment: DocumentAssignment = new DocumentAssignment(
            this.companyDocument!,
            this.slotAssignments
        );

        if (this.documentAssignmentService.hasDuplicateSlotAssignments(documentAssignment)) {
            this.notify.error('document.cannotAssignEmployeeToMultipleRoles');
            return;
        }

        this.isLoading.emit(true);
        this.closeDialog();

        await Promise.all(this.documentAssignmentService.updateSlotAssignments(documentAssignment));

        form.reset();
        this.notify.success('document.defaultCosignersSaved');
        this.reload.emit();
        this.isLoading.emit(false);
    }

    async onSaveMultiSignerAssignment(form: NgForm): Promise<void> {
        form.onSubmit(null);

        if (!form.valid) {
            return;
        }

        const documentAssignment: DocumentAssignment = new DocumentAssignment(
            this.companyDocument,
            this.slotAssignments
        );

        if (this.documentAssignmentService.hasDuplicateSlotAssignments(documentAssignment)) {
            this.notify.error('document.cannotAssignEmployeeToMultipleRoles');
            return;
        }

        this.isLoading.emit(true);
        this.closeDialog();

        // Update all the slot assignments
        // This is not directly related to assigning the document
        // But we need to remember the order for next time
        try {
            await Promise.all(this.documentAssignmentService.updateSlotAssignments(documentAssignment));

            await this.documentAssignmentService.createSignatureRequests(documentAssignment);

            // we differentiate between the assign view and preview view for Pendo
            const event = this.router.url.includes('assigned')
                ? AnalyticEvents.MultiAssignFromEmployeesAssigned
                : AnalyticEvents.MultiAssignFromDocPreview;

            const customFieldCount = this.companyDocument?.signingTemplate.numberOfCustomFields();
            this.analyticService.trackEvent(event, { customFieldCount });
            form.reset();
            this.notify.success('document.signatureRequestCreated');
            this.reload.emit();
        } catch (error) {
            // this should not happen
            // if it does, we stop the loading spinner and rethrow
            // to the global handler so the error gets to sentry
            this.notify.error(error);
            this.isLoading.emit(false);
            throw error;
        }
    }

    onDownload(): void {
        if (this.companyDocument?.isPublic) {
            this.analyticService.trackEvent(AnalyticEvents.SelfProfilePublicDocumentsDownloaded);
        }
        this.fileHelper.save(
            this.companyDocument?.file.id,
            this.companyDocument?.name,
            this.companyDocument?.file.type
        );
    }

    onDetails(): void {
        if (this.companyDocument?.isPublic) {
            this.analyticService.trackEvent(AnalyticEvents.SelfProfilePublicDocumentsOpenedDetails);
        }
        this.currentlyOpenDialog = 'DETAILS';
    }

    async onDeleteCompanyDocument(): Promise<void> {
        const shouldDelete = await this.companyDocumentDeletionModal.open(this.companyDocument);

        if (!shouldDelete) {
            return;
        }

        try {
            await this.companyDocument?.delete();
            this.notify.success('document.documentDeleted');
            this.router.navigate([this.companyDocumentBackRoute]);
        } catch (error) {
            this.notify.error(error);
        }
    }

    onCancel(): void {
        this.companyDocument?.revert();
        this.closeDialog();
    }

    onCancelDefaultCosigners(defaultCosignersForm: MultiSignerAssignmentForm): void {
        defaultCosignersForm.reset();
        this.closeDialog();
    }

    onCancelMultiSigner(): void {
        this.multiSignerAssignmentForm.reset();
        this.closeDialog();
    }

    closeDialog(): void {
        this.currentlyOpenDialog = undefined;
    }

    isTemplateReady(): boolean {
        return this.companyDocument?.isTemplateEditable() || this.becameEditable;
    }

    openDefaultCosigners(form: NgForm, defaultCosignersForm: MultiSignerAssignmentForm): void {
        // We only want to reset this data if the form has been reset or is fresh
        if (!form.submitted) {
            defaultCosignersForm.setup();
        }

        this.setHasCustomFieldAssignments();
        this.currentlyOpenDialog = 'DEFAULT_COSIGNERS';
    }

    updateSlots(slots: SlotAssignment[]): void {
        this.slotAssignments = slots;
    }

    multiAssignDocument(form: NgForm, multiSignerAssignmentForm: MultiSignerAssignmentForm): void {
        if (this.companyDocument?.isSigningDocument() && !this.companyDocument.signingTemplate.hasHelloSignTemplate()) {
            this.lackingSigningTemplateModal.open().then((response) => {
                if (response) {
                    const url = `signingTemplate/${this.companyDocument?.signingTemplate.id}`;
                    this.router.navigate([url], { relativeTo: this.route });
                }
            });

            return;
        }

        if (!this.companyDocument?.isBulkAssignable()) {
            multiSignerAssignmentForm.setup();
            this.currentlyOpenDialog = 'MULTI_SIGNER';
            return;
        }

        this.bulkAssignDocument();
    }

    private bulkAssignDocument(): void {
        const currentlyAssigned = [...this.companyDocument.employeesWithPendingAssignments];

        this.setHasCustomFieldAssignments();

        this.appEmployeeSelection.show<Employee[]>(currentlyAssigned).then((newSelection: Employee[]) => {
            if (!newSelection) {
                return;
            }

            this.isLoading.emit(true);
            this.companyDocument
                ?.assignAndUnassign(newSelection)
                .then(() => {
                    this.reload.emit();

                    if (this.companyDocument?.isSigningDocument()) {
                        // we differentiate between the assign view and preview view for Pendo
                        const event = this.router.url.includes('assigned')
                            ? AnalyticEvents.BulkAssignFromEmployeesAssigned
                            : AnalyticEvents.BulkAssignFromDocPreview;
                        const customFieldCount = this.companyDocument.signingTemplate.numberOfCustomFields();
                        this.analyticService.trackEvent(event, { customFieldCount });
                    }
                })
                .catch((err) => {
                    // this should not happen
                    // if it does, we stop the loading spinner and rethrow
                    // to the global handler so the error gets to sentry
                    this.notify.error(err || 'document.unableToAssign');
                    this.isLoading.emit(false);
                    throw err;
                });
        });
    }

    private setHasCustomFieldAssignments(): void {
        this.companyDocument?.signingTemplate?.slots.forEach((slot) => {
            if (slot.customFieldAssignments?.length) {
                this.hasCustomFieldAssignments = true;
                return;
            }
        });
    }
}
